<template>
  <v-card flat>
    <v-card-text>
      <v-form ref="companyNameForm">
        <v-row>
          <v-col cols="12">
            <v-text-field
              ref="companyUrl"
              v-model="tempCompany.url"
              label="Company domain name"
              :rules="[rules.domain]"
              hide-details="auto"
            >
              <template #append>
                <v-fade-transition leave-absolute>
                  <v-progress-circular
                    v-if="searchByUrlLoading"
                    size="24"
                    color="info"
                    indeterminate
                  />
                </v-fade-transition>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12">
            <v-text-field
              ref="companyName"
              v-model="tempCompany.name"
              label="Company Name"
              :rules="[rules.name]"
              :loading="loadingCompanies"
              hide-details="auto"
              autocomplete="off"
            />
          </v-col>
        </v-row>
        <v-card
          v-if="companies.length"
          variant="outlined"
          style="overflow: hidden; position: absolute; z-index: 6; width: 80%; left: 10%;"
        >
          <v-toolbar
            v-if="isCompanyFoundBySearch && isFormDisabled"
            type="info"
            flat
            color="primary"
          >
            <v-toolbar-title>We found some matches</v-toolbar-title>
            <v-spacer />
            {{ companies.length }} Companies
          </v-toolbar>
          <v-virtual-scroll
            :bench="benched"
            :items="companies"
            :height="companies.length > 2 ? 300 : companies.length === 2 ? 200 : 100"
            item-height="100"
            class="mt-3"
          >
            <template #default="{ item }">
              <v-list-item :key="item._key" :ripple="true">
                <div>
                  <v-list-item-title>
                    <div class="record-id">Company ID# {{ item._key }}</div>
                    {{ item.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.address.street }}, {{ item.address.city }} {{ item.address.state }} {{ item.address.postal }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle>
                    {{ item.url }}
                  </v-list-item-subtitle>
                </div>

                <template #append>
                  <v-btn
                    icon
                    :to="getAccountRoute({ name: 'company', params: { id: item._key }})"
                    target="_blank"
                  >
                    <v-icon size="small">
                      mdi-open-in-new
                    </v-icon>
                  </v-btn>
                </template>
              </v-list-item>
            </template>
          </v-virtual-scroll>
          <template v-if="isCompanyFoundBySearch && isFormDisabled && tempCompany.url">
            <v-divider />
            <v-card-actions>
              Sorry, the provided URL is already in use by another company.
              Please choose a different URL for your company.
            </v-card-actions>
          </template>
          <template v-if="tempCompany.name && !tempCompany.url">
            <v-divider />
            <div class="pa-4">
              <span class="cursor-pointer text-primary" @click="createNewCompanyHandler">
                Cant find your company? Create a new one</span>
            </div>
          </template>
        </v-card>
      </v-form>
      <!-- The details stay under an overlay until the lookup above either finds
           nothing or the user opts to create a new company. -->
      <v-form ref="companyForm" class="position-relative mt-6">
        <!-- Under the panel's docked actions bar (z-index 7): the overlay stack
             put the veil over Cancel / Save. The z-index prop is no help here,
             since inside the Add Company dialog Vuetify stacks the overlay at
             the dialog's z-index + 10 regardless; the style prop wins over it. -->
        <v-overlay
          contained
          :style="{ zIndex: 6 }"
          :opacity="0.8"
          scrim="surface"
          :model-value="overlay"
          class="align-center justify-center form-field-overlay"
        >
          <v-card
            theme="dark"
            variant="outlined"
            class="bg-sc-surface-header mt-6"
          >
            <v-card-text>
              <span class="subtitle-1">{{ getMessage }}</span>
            </v-card-text>
            <v-card-actions v-if="errorMessage" class="pt-0">
              <v-spacer />
              <v-btn color="primary" @click="overlay = false">Enter company details</v-btn>
            </v-card-actions>
          </v-card>
        </v-overlay>
        <v-defaults-provider :defaults="fieldDefaults">
          <v-row>
            <v-col cols="12" sm="auto">
              <v-img
                :src="tempCompany.logo || $placeholderCompanyImg"
                alt="Company logo"
                width="136"
                height="136"
                rounded
                :cover="false"
                eager
              />
            </v-col>
            <v-col class="d-flex flex-column justify-center ga-6">
              <v-autocomplete
                v-model="tempCompany.industry_group"
                label="Industry Group"
                class="flex-grow-0"
                :items="industryGroupTypes"
                clearable
                autocomplete="off"
              />
              <v-autocomplete
                v-model="tempCompany.industry"
                label="Industry"
                class="flex-grow-0"
                :disabled="!tempCompany.industry_group"
                :items="companyIndustryTypes"
                clearable
                autocomplete="off"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pb-0">
              <div class="text-overline text-medium-emphasis">Address</div>
            </v-col>
          </v-row>
          <CommonAddressWidget
            v-model="tempCompany.address"
            show-suite
            class="pa-0"
            source="company"
          />
          <v-row>
            <v-col cols="12" class="pb-0">
              <div class="text-overline text-medium-emphasis">Contact</div>
            </v-col>
            <v-col cols="12" sm="6">
              <CommonPhonePicker
                show-extension
                :phone-number="tempCompany.phone.office"
                :phone-extension="tempCompany.phone.office_ext"
                :phone-country-code="tempCompany.phone.office_country_code"
                @save:number="tempCompany.phone.office = $event"
                @save:extension="tempCompany.phone.office_ext = $event"
                @save:countryCode="tempCompany.phone.office_country_code = $event"
              >
                <template #default="{ props }">
                  <v-text-field
                    v-model="tempCompany.phone.office"
                    v-facade="getCountryMask(tempCompany.phone.office_country_code)"
                    :suffix="tempCompany.phone.office_ext ? `ext: ${tempCompany.phone.office_ext}` : null"
                    label="Office Phone"
                    v-bind="props"
                  />
                </template>
              </CommonPhonePicker>
            </v-col>
            <v-col cols="12" sm="6">
              <CommonPhonePicker
                show-extension
                :phone-number="tempCompany.phone.fax"
                :phone-extension="tempCompany.phone.fax_ext"
                :phone-country-code="tempCompany.phone.fax_country_code"
                @save:number="tempCompany.phone.fax = $event"
                @save:extension="tempCompany.phone.fax_ext = $event"
                @save:countryCode="tempCompany.phone.fax_country_code = $event"
              >
                <template #default="{ props }">
                  <v-text-field
                    v-model="tempCompany.phone.fax"
                    v-facade="getCountryMask(tempCompany.phone.fax_country_code)"
                    :suffix="tempCompany.phone.fax_ext ? `ext: ${tempCompany.phone.fax_ext}` : null"
                    label="Office Fax"
                    v-bind="props"
                  />
                </template>
              </CommonPhonePicker>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pb-0">
              <div class="text-overline text-medium-emphasis">Company Details</div>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="tempCompany.metrics.employees"
                label="Employees"
                autocomplete="off"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="tempCompany.year_established"
                label="Year Established"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="tempCompany.metrics.annualRevenue"
                label="Annual Revenue"
                autocomplete="off"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="tempCompany.metrics.estimatedAnnualRevenue"
                label="Estimated Annual Revenue"
                autocomplete="off"
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="tempCompany.description"
                label="Description"
                rows="3"
                auto-grow
              />
            </v-col>
          </v-row>
          <!-- The section pads itself as a card title/text; mx-n4 lines its
               text up with the fields above. -->
          <CompanyBrokerageSection
            v-model="tempCompany.brokerLicense"
            v-model:is-brokerage="tempCompany.isBrokerage"
            class="mx-n4 mt-3"
          />
        </v-defaults-provider>
      </v-form>
    </v-card-text>
    <v-card-actions class="sc-navigation-drawer-actions">
      <v-spacer />
      <v-btn
        variant="text"
        class="cancel"
        :size="$vuetify.display.smAndDown ? 'small' : undefined"
        @click="cancelNewCompany"
      >
        Cancel
      </v-btn>
      <v-btn
        v-if="isCompanyFoundBySearch && isFormDisabled"
        color="primary"
        class="px-4"
        :disabled="isCompanyFoundBySearch"
        :size="$vuetify.display.smAndDown ? 'small' : undefined"
        @click="isFormDisabled = false"
      >
        Create New Company
      </v-btn>
      <v-btn
        v-else
        color="primary"
        class="px-4"
        :loading="isLoading"
        :disabled="isSaveCompanyBtnDisable"
        :size="$vuetify.display.smAndDown ? 'small' : undefined"
        data-track="company.create.save"
        @click="saveCompany"
      >
        Save
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import { debounce } from 'lodash-es';

import { TOGGLE_SPLASH_SCREEN } from '@/store/action-types';
import { eventBus } from '@/eventbus/eventbus';
import { getCompanyDefaultObj } from '@/bundles/Companies/helpers/companyDefaultData';
import { ServiceFactory } from '@/services/ServiceFactory';
import { companyService } from '@/bundles/Companies/factory/companyServiceFactory';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { RequestAbortMixin } from '@/bundles/App/mixins/RequestAbortMixin';
import { AppFormMixin } from '@/bundles/App/mixins/forms/AppFormMixin';
import { CountriesMasksMixin } from '@/bundles/Common/mixins/CountriesMasksMixin';
import { useNotifications } from '@/bundles/App/composables/useNotifications';
import { trackActionSuccess } from '@/bundles/Analytics/helpers/gtm';
import { KeyActionEnum } from '@/bundles/Analytics/types';

import CommonPhonePicker from '@/bundles/Common/components/CommonPhonePicker';
import CompanyBrokerageSection from '@/bundles/Companies/components/forms/CompanyBrokerageSection.vue';
import CommonAddressWidget from '@/components/common-components/address/CommonAddressWidget.vue';

const CompanyService = ServiceFactory.get('company');

const FIELD_DEFAULTS = Object.freeze({ global: { hideDetails: 'auto' } });

export default {
  name: 'CompanyAddForm',

  components: {
    CommonAddressWidget,
    CommonPhonePicker,
    CompanyBrokerageSection,
  },

  mixins: [AccountLayoutMixin, RequestAbortMixin, AppFormMixin, CountriesMasksMixin],

  props: {
    sourceType: {
      type: String,
      default: '',
    },
    sourceId: {
      type: [String, Number],
      default: '',
    },
  },

  emits: ['cancelCompany', 'closeDialog', 'companyAdded', 'updatePropertyHandler'],

  setup () {
    const { addNotification } = useNotifications();

    return { addNotification };
  },

  data: () => ({
    companies: [],
    loadingCompanies: false,
    isFormDisabled: true,
    rules: {
      name: (value) => (!!value && value.length > 1) || 'Name length must be more than 1 characters',
      domain: value => {
        if (!value) { return true; }

        const pattern = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g;
        return pattern.test(value) || 'Invalid domain name';
      },
    },
    tempCompany: getCompanyDefaultObj(),
    defaultCompany: getCompanyDefaultObj(),
    benched: 0,
    overlay: true,
    searchByUrlLoading: false,
    errorMessage: null,
    isLoading: false,
  }),

  computed: {
    ...mapState({
      token: (state) => state.idToken,
      industryGroupTypes: state => state.Company.industryGroupTypes,
      industryTypes: state => state.Company.industryTypes,
    }),
    companyIndustryTypes () {
      const type = this.industryTypes.find(
        (item) => item.group === this.tempCompany.industry_group
      );
      return type ? type.types : [];
    },
    isCompanyFoundBySearch () {
      return !!this.companies.length && !this.loadingCompanies;
    },
    isSaveCompanyBtnDisable () {
      return !this.tempCompany.name || this.tempCompany.name.length <= 1 || this.loadingCompanies;
    },
    getMessage () {
      return this.errorMessage || 'Enter a company name or domain name to get started';
    },
    fieldDefaults: () => FIELD_DEFAULTS,
  },

  watch: {
    'tempCompany.name' (value) {
      if (!!value && value.length > 1) {
        this.isFormDisabled = true;
        this.searchCompaniesDebounced(this);
      }
    },
    async 'tempCompany.url' () {
      const { valid: isCompanyDomainValid } = await this.$refs.companyUrl.validate();

      if (!isCompanyDomainValid || !this.tempCompany.url) {
        return;
      }

      this.searchCompaniesByUrlDebounced(this);
    },
  },

  mounted () {
    eventBus.$on('clearCompany', this.clearCompany);
    setTimeout(() => { // next tick not working
      this.$refs.companyUrl.focus();
    }, 200);
  },

  beforeUnmount () {
    eventBus.$off('clearCompany', this.clearCompany);
  },

  methods: {
    ...mapMutations({
      toggleSplashScreen: TOGGLE_SPLASH_SCREEN,
    }),
    searchCompaniesDebounced: debounce(async function (vm) {
      await vm.searchCompanies();
    }, 400),
    searchCompanies: async function () {
      try {
        this.overlay = true;
        this.loadingCompanies = true;

        const res = await CompanyService.list({
          token: this.token,
          params: [
            { name: 'sort', value: 'name' },
            { name: 'descending', value: 'desc' },
            { name: 'url', value: this.tempCompany.url },
            { name: 'company_name', value: this.tempCompany.name }
          ],
        });
        const { result: companies } = res.data;
        if (companies.length > 20) {
          companies.length = 20;
        }
        this.companies = companies;
        this.overlay = this.companies.length > 0;
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.loadingCompanies = false;
      }
    },
    searchCompaniesByUrlDebounced: debounce(async function (vm) {
      await vm.searchCompaniesByUrlHandler();
    }, 400),
    async searchCompaniesByUrlHandler () {
      try {
        this.searchByUrlLoading = true;

        this.handleRequestAbort();
        const company = await companyService.getCompanyByEnrich({
          params: [{ name: 'domain', value: this.tempCompany.url }],
          controller: this.abortController
        });

        if (typeof company === 'object' && company !== null) {
          this.tempCompany = { ...this.tempCompany, ...company, url: this.tempCompany.url };
          return;
        }

        this.companies = [];

        this.errorMessage = company || null;
      } catch (error) {
        if (error.message === 'canceled') {
          return;
        }

        this.addNotification({ ...error, status: 0 });
        this.errorMessage = error.message;
      } finally {
        this.searchByUrlLoading = false;
      }
    },
    saveCompany () {
      if (this.tempCompany._key) {
        this.updateCompany();
      } else {
        this.saveNewCompany();
      }
    },
    async saveNewCompany () {
      const { valid } = await this.$refs.companyForm.validate();
      if (!valid) {
        this.showFormValidationErrorNotification();
        return;
      }

      this.isLoading = true;
      const submitDate = Date.now();

      const formData = {
        date_insert: submitDate,
        date_update: submitDate,
        name: this.tempCompany.name,
        logoSource: this.tempCompany.logo,
        logoUrl: this.tempCompany.logo,
        url: this.tempCompany.url,
        industry: this.tempCompany.industry,
        industry_group: this.tempCompany.industry_group,
        description: this.tempCompany.description,
        address: { ...this.tempCompany.address },
        phone: { ...this.tempCompany.phone },
        metrics: { ...this.tempCompany.metrics },
        isBrokerage: this.tempCompany.isBrokerage,
        brokerLicense: {
          legal_name: this.tempCompany.brokerLicense.legal_name,
          number: this.tempCompany.brokerLicense.number,
          state: this.tempCompany.brokerLicense.state,
          taxId: this.tempCompany.brokerLicense.taxId
        },
        year_established: this.tempCompany.year_established
      };

      try {
        const { data } = await CompanyService.create({ token: this.token, payload: formData });

        if (data) {
          trackActionSuccess(KeyActionEnum.CompanyCreate);
          this.$emit('updatePropertyHandler', data);
          this.$emit('companyAdded', data);
          this.$emit('closeDialog', data);
          this.clearCompany();
        }
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.isLoading = false;
      }
    },
    cancelNewCompany () {
      this.clearCompany();
      this.$emit('cancelCompany');
    },
    uploadSuccess (result) {
      this.tempCompany.logo = result.filesUploaded[0].url;
    },
    clearCompany () {
      this.$refs.companyNameForm.reset();
      this.$refs.companyForm.reset();
      setTimeout(() => {
        this.tempCompany = Object.assign({}, this.defaultCompany);
        this.toggleSplashScreen(false);
      }, 500);
    },
    createNewCompanyHandler () {
      this.isFormDisabled = false;
      this.overlay = false;
      this.companies = [];
    }
  },
};
</script>
